<template>
  <div class="protocol-version-page">
    <div class="page-header">
      <div>
        <h2>协议版本管理</h2>
        <p>统一管理协议发布版本、生效时间与历史版本</p>
      </div>
      <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">新增协议版本</el-button>
    </div>

    <table-template
      ref="protocolTable"
      :queryParams="queryParams"
      actionUrl="/businessAgreement/findAgreementList.action"
    >
      <template slot="item">
        <el-table-column label="协议名称/版本" min-width="230">
          <template slot-scope="scope">
            <div class="agreement-name">{{ scope.row.agreementName || '-' }}</div>
            <div class="version-number">{{ scope.row.versionNo || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="协议类型" width="120">
          <template slot-scope="scope">{{ agreementTypeText(scope.row.type) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="scope">
            <el-tag :type="String(scope.row.status) === '1' ? 'success' : 'info'" size="small">
              {{ String(scope.row.status) === '1' ? '生效中' : '已停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="发布日期/生效时间" min-width="180">
          <template slot-scope="scope">{{ scope.row.effectiveTime | dateTimeFormat }}</template>
        </el-table-column>
        <el-table-column label="已签署用户" prop="signedUserCount" width="130" />
        <el-table-column label="创建人" prop="createUserName" min-width="130" />
        <el-table-column label="操作" width="230" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="showContent(scope.row)">协议正文</el-button>
            <el-button type="text" @click="openEditDialog(scope.row)">修改</el-button>
            <el-button
              v-confirm
              type="text"
              :class="String(scope.row.status) === '1' ? 'disable-button' : 'enable-button'"
              :confirm-text="String(scope.row.status) === '1' ? '确定停用该协议版本？' : '确定启用该协议版本？'"
              requestUrl="/businessAgreement/changeAgreementStatus.action"
              :confirm-data="{ id: scope.row.id, status: String(scope.row.status) === '1' ? '2' : '1' }"
              @onConfirm="onStatusChanged"
            >{{ String(scope.row.status) === '1' ? '停用' : '启用' }}</el-button>
          </template>
        </el-table-column>
      </template>
    </table-template>

    <el-dialog
      :title="editing ? '修改协议版本' : '新增协议版本'"
      :visible.sync="createVisible"
      width="680px"
      :close-on-click-modal="false"
      @closed="resetCreateForm"
    >
      <div v-if="!editing" class="dialog-tip">发布后立即生效，当前生效版本将自动停用。</div>
      <el-form ref="createForm" :model="createForm" :rules="rules" label-position="top">
        <el-form-item label="协议名称" prop="agreementName">
          <el-input v-model.trim="createForm.agreementName" maxlength="100" placeholder="请输入协议名称" />
        </el-form-item>
        <el-form-item label="协议类型" prop="type">
          <el-select v-model="createForm.type" placeholder="请选择协议类型" style="width: 100%;">
            <el-option label="用户协议" value="1" />
            <el-option label="隐私政策" value="2" />
            <el-option label="出口协议" value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="版本号" prop="versionNo">
          <el-input v-model.trim="createForm.versionNo" maxlength="30" placeholder="例如：V1.2" />
        </el-form-item>
        <el-form-item label="本次变更摘要" prop="changeSummary">
          <el-input v-model.trim="createForm.changeSummary" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="请输入本次变更摘要" />
        </el-form-item>
        <el-form-item label="协议正文" prop="content">
          <quill-editor
            ref="contentEditor"
            v-model="createForm.content"
            class="agreement-editor"
            :options="editorOption"
            @change="validateContent"
          />
        </el-form-item>
        <el-form-item prop="isRequireReSign">
          <el-checkbox v-model="requireReSign">要求存量用户补签</el-checkbox>
          <div class="checkbox-tip">发布后向未签署新版本的已登录老用户展示协议更新弹窗</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitAgreement">{{ editing ? '保存修改' : '确认发布并生效' }}</el-button>
      </span>
    </el-dialog>

    <el-dialog title="协议正文" :visible.sync="contentVisible" width="680px">
      <div class="content-title">{{ currentAgreement.agreementName }} {{ currentAgreement.versionNo }}</div>
      <div v-if="currentAgreement.content" class="agreement-content ql-editor" v-html="currentAgreement.content"></div>
      <div v-else class="agreement-content">暂无协议正文</div>
    </el-dialog>
  </div>
</template>

<script>
import TableTemplate from '@/components/TableTemplate'
import BasicPager from '@/mixins/BasicPager'
import BasicModel from '@/framework/basic/BasicModel'
import { quillEditor } from 'vue-quill-editor'

const emptyForm = () => ({
  agreementName: '',
  type: '',
  versionNo: '',
  changeSummary: '',
  content: '',
  isRequireReSign: '1'
})

export default {
  name: 'protocolVersion',
  components: { TableTemplate, quillEditor },
  extends: BasicPager,
  data () {
    return {
      queryParams: {},
      createVisible: false,
      editing: false,
      contentVisible: false,
      submitting: false,
      requireReSign: true,
      currentAgreement: {},
      createForm: emptyForm(),
      editorOption: {
        theme: 'snow',
        placeholder: '请粘贴或编辑完整协议正文',
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }],
            ['blockquote', 'link'],
            ['clean']
          ]
        }
      },
      rules: {
        agreementName: [{ required: true, message: '请输入协议名称', trigger: 'blur' }],
        type: [{ required: true, message: '请选择协议类型', trigger: 'change' }],
        versionNo: [{ required: true, message: '请输入版本号', trigger: 'blur' }],
        changeSummary: [{ required: true, message: '请输入本次变更摘要', trigger: 'blur' }],
        content: [{ validator: (rule, value, callback) => this.validateRichText(value, callback), trigger: 'change' }]
      }
    }
  },
  methods: {
    openCreateDialog () {
      this.editing = false
      this.createVisible = true
    },
    openEditDialog (row) {
      this.editing = true
      this.createForm = {
        id: row.id,
        agreementName: row.agreementName || '',
        type: row.type === null || row.type === undefined ? '' : String(row.type),
        versionNo: row.versionNo || '',
        changeSummary: row.changeSummary || '',
        content: row.content || '',
        isRequireReSign: String(row.isRequireReSign) === '1' ? '1' : '0'
      }
      this.requireReSign = String(row.isRequireReSign) === '1'
      this.createVisible = true
    },
    resetCreateForm () {
      this.createForm = emptyForm()
      this.requireReSign = true
      this.editing = false
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.clearValidate()
      })
    },
    submitAgreement () {
      this.$refs.createForm.validate(valid => {
        if (!valid || this.submitting) return
        this.submitting = true
        const params = Object.assign({}, this.createForm, {
          isRequireReSign: this.requireReSign ? '1' : '0'
        })
        if (!this.editing) {
          params.createUserId = this.getLoginAccountId()
          params.createUserName = this.loginInfo.realName
        }
        const action = this.editing
          ? '/businessAgreement/updateAgreement.action'
          : '/businessAgreement/publishAgreement.action'
        const successMessage = this.editing ? '协议版本修改成功' : '协议版本发布成功'
        const errorMessage = this.editing ? '协议版本修改失败' : '协议版本发布失败'
        BasicModel.post(this, action, params, {
          success: () => {
            this.$message.success(successMessage)
            this.createVisible = false
            this.$refs.protocolTable.search()
            this.submitting = false
          },
          error: response => {
            this.submitting = false
            const message = response && response.data && response.data.errMsg
            this.$message.error(message || errorMessage)
          }
        })
      })
    },
    validateRichText (value, callback) {
      const text = (value || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim()
      callback(text ? undefined : new Error('请输入协议正文'))
    },
    validateContent () {
      if (this.$refs.createForm) this.$refs.createForm.validateField('content')
    },
    showContent (row) {
      this.currentAgreement = row
      this.contentVisible = true
    },
    agreementTypeText (type) {
      const typeMap = {
        '1': '用户协议',
        '2': '隐私政策',
        '3': '出口协议'
      }
      return typeMap[String(type)] || '-'
    },
    onStatusChanged () {
      this.$message.success('协议状态修改成功')
      this.$refs.protocolTable.search()
    }
  }
}
</script>

<style scoped>
.protocol-version-page { padding: 24px; }
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.page-header h2 { margin: 0 0 8px; color: #303133; font-size: 24px; }
.page-header p { margin: 0; color: #909399; }
.agreement-name { color: #303133; font-weight: 600; line-height: 24px; }
.version-number { color: #909399; line-height: 22px; }
.dialog-tip { margin: -12px 0 18px; padding-bottom: 16px; color: #909399; border-bottom: 1px solid #ebeef5; }
.checkbox-tip { padding-left: 25px; color: #909399; line-height: 22px; }
.content-title { margin-bottom: 16px; color: #303133; font-size: 18px; font-weight: 600; }
.agreement-content { max-height: 55vh; overflow-y: auto; color: #606266; line-height: 1.8; white-space: pre-wrap; word-break: break-word; }
.agreement-editor { margin-bottom: 42px; }
.agreement-editor /deep/ .ql-container { min-height: 220px; font-size: 14px; }
.agreement-editor /deep/ .ql-editor { min-height: 220px; }
.disable-button { color: #f56c6c; }
.enable-button { color: #67c23a; }
</style>
